import { useLazyGetConditionComparisonsQuery } from 'store/services/flow/agendaLookups';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { createRandomString } from 'utils/common';
import type { ComparisonType, SourceType } from 'types';

export type Condition = {
  localId: string;
  localGroupId: string | null;
  id: string | null;
  previousConditionId: string | null;
  groupId: string | null;
  parentGroupId: string | null;
  logicalOperator: 'And' | 'Or' | 'None';
  sourceType: string;
  sourceFormItemId: string | null | undefined;
  comparisonType: string;
  valueDirect: string | null | undefined;
  valueSourceType: string | null | undefined;
  valueFormItemId: string | null | undefined;
  parenthesis: {
    conditionGroupId: string | null;
    parentConditionGroupId: string | null;
    openingParenthesisCount: number;
    closingParenthesisCount: number;
  };
  isGroup: boolean;
};

export interface UseConditionsProps {
  conditions?: Condition[];
  isLoading?: boolean;
  agendaId?: string;
}

type ConditionsData = Condition[];
const comparisonsTypeWhichDoesNotNeedValue = ['IsEmpty', 'IsNotEmpty', 'IsPressed', 'IsChecked', 'IsUnchecked'];

export const useConditions = (props?: { conditions?: Condition[]; isLoading?: boolean; agendaId?: string }) => {
  const [conditionsData, setConditionsData] = useState<ConditionsData>(props?.conditions || []);
  const [isComparisonsLoading, setIsComparisonsLoading] = useState(false);
  const [getConditionComparisons] = useLazyGetConditionComparisonsQuery();
  const [comparisons, setComparisons] = useState<{ [key: string]: ComparisonType[] }>({});
  const { id: agendaId } = useParams();

  const getComparisons = useCallback(
    async (condition: Condition) => {
      if (props?.isLoading) return;

      setIsComparisonsLoading(true);

      try {
        const res = await getConditionComparisons({
          agendaId: props?.agendaId ?? agendaId ?? '',
          sourceType: condition.sourceType as SourceType,
          sourceFormItemId: condition.sourceType === 'FormItem' ? (condition.sourceFormItemId ?? undefined) : undefined,
        });

        if (res.error) {
          console.error('Failed to fetch comparisons:', res.error);
        } else {
          setComparisons((prev) => ({
            ...prev,
            [condition.id || condition.localId]: res.data?.comparisons ?? [],
          }));
        }
      } catch (error) {
        /* empty */
      } finally {
        setIsComparisonsLoading(false);
      }
    },
    [props?.agendaId, props?.isLoading, agendaId, getConditionComparisons]
  );

  const transformBackendConditions = (backendConditions: Condition[]) => {
    const result: Condition[] = [];
    const groupStack: string[] = [];

    backendConditions.forEach((condition) => {
      const conditionId = condition.id || createRandomString(19);
      const hasOpeningparenthesis = condition.parenthesis.openingParenthesisCount > 0;
      const hasClosingparenthesis = condition.parenthesis.closingParenthesisCount > 0;

      const parentGroupId = groupStack.length ? groupStack[groupStack.length - 1] : null;

      if (hasOpeningparenthesis) {
        groupStack.push(conditionId);
      }

      const conditionItem = {
        ...condition,
        localId: conditionId,
        isGroup: hasOpeningparenthesis,
        groupId: hasOpeningparenthesis ? conditionId : parentGroupId,
        parentGroupId: hasOpeningparenthesis ? parentGroupId : parentGroupId,
      };

      result.push(conditionItem);

      getComparisons(conditionItem);

      if (hasClosingparenthesis) {
        for (let i = 0; i < condition.parenthesis.closingParenthesisCount; i++) {
          groupStack.pop();
        }
      }
    });

    return result;
  };

  useEffect(() => {
    if (props?.conditions && !props.isLoading) {
      const processedConditions = transformBackendConditions(props.conditions);
      setConditionsData(processedConditions);
    }
  }, [props?.conditions, props?.isLoading]);

  const recalculateparenthesis = () => {
    setConditionsData((prevData) => {
      const updatedData = prevData.map((item) => ({
        ...item,
        parenthesis: {
          ...item.parenthesis,
          openingParenthesisCount: 0,
          closingParenthesisCount: 0,
        },
      }));

      const recursiveCalculate = (groupId: string | null, nestingLevel: number): void => {
        const groupIndex = groupId ? updatedData.findIndex((c) => c.localId === groupId) : -1;

        if (groupIndex !== -1) {
          updatedData[groupIndex] = {
            ...updatedData[groupIndex],
            parenthesis: {
              ...updatedData[groupIndex].parenthesis,
              openingParenthesisCount: 1,
            },
          };
        }

        const itemsInGroup = updatedData.filter((item) => {
          return item.isGroup ? item.parentGroupId === groupId : item.groupId === groupId;
        });

        if (itemsInGroup.length === 0 && groupIndex !== -1) {
          updatedData[groupIndex] = {
            ...updatedData[groupIndex],
            parenthesis: {
              ...updatedData[groupIndex].parenthesis,
              closingParenthesisCount: nestingLevel,
            },
          };
          return;
        }

        itemsInGroup.forEach((item, index) => {
          const itemIndex = updatedData.findIndex((c) => c.localId === item.localId);

          if (item.isGroup) {
            recursiveCalculate(item.localId, nestingLevel + 1);
          }

          if (index === itemsInGroup.length - 1 && !item.isGroup) {
            updatedData[itemIndex] = {
              ...updatedData[itemIndex],
              parenthesis: {
                ...updatedData[itemIndex].parenthesis,
                closingParenthesisCount: nestingLevel,
              },
            };
          }
        });
      };

      const topLevelGroups = updatedData.filter((item) => item.isGroup && item.parentGroupId === null);
      topLevelGroups.forEach((group) => {
        recursiveCalculate(group.localId, 1);
      });

      return updatedData;
    });
  };

  const onAddCondition = (addInside: boolean = false, groupId?: string | null) => {
    setConditionsData((prevData) => {
      const newCondition: Condition = {
        localId: createRandomString(19),
        localGroupId: createRandomString(19),
        id: null,
        previousConditionId: null,
        groupId: addInside ? String(groupId) : null,
        parentGroupId: addInside ? String(groupId) : null,
        logicalOperator: prevData.length === 0 ? 'None' : 'And',
        sourceType: '',
        sourceFormItemId: null,
        comparisonType: '',
        valueDirect: null,
        valueSourceType: null,
        valueFormItemId: null,
        parenthesis: {
          conditionGroupId: null,
          parentConditionGroupId: null,
          openingParenthesisCount: 0,
          closingParenthesisCount: 0,
        },
        isGroup: false,
      };

      const updatedData = [...prevData];

      if (addInside && groupId) {
        const lastConditionIndex = updatedData.reduce((lastIndex, condition, index) => {
          if (condition.groupId === groupId && !condition.isGroup) {
            return index;
          }
          return lastIndex;
        }, -1);

        if (lastConditionIndex !== -1) {
          updatedData.splice(lastConditionIndex + 1, 0, newCondition);
        } else {
          const groupIndex = updatedData.findIndex((condition) => condition.localId === groupId);
          updatedData.splice(groupIndex + 1, 0, newCondition);
        }
      } else {
        updatedData.push(newCondition);
      }

      return updatedData;
    });

    recalculateparenthesis();
  };

  const onCreateGroup = (parentGroupId?: string | null, position?: number) => {
    const localGroupId = createRandomString(19);

    const newGroupCondition: Condition = {
      localId: localGroupId,
      localGroupId: localGroupId,
      id: null,
      previousConditionId: null,
      groupId: localGroupId,
      parentGroupId: parentGroupId || null,
      logicalOperator: conditionsData.length === 0 ? 'None' : 'And',
      sourceType: '',
      sourceFormItemId: null,
      comparisonType: '',
      valueDirect: null,
      valueSourceType: null,
      valueFormItemId: null,
      parenthesis: {
        conditionGroupId: localGroupId,
        parentConditionGroupId: parentGroupId || null,
        openingParenthesisCount: 0,
        closingParenthesisCount: 0,
      },
      isGroup: true,
    };

    setConditionsData((prevData) => {
      const updatedData = [...prevData];
      if (position !== undefined) {
        updatedData.splice(position, 0, newGroupCondition);
      } else {
        updatedData.push(newGroupCondition);
      }
      return updatedData;
    });

    recalculateparenthesis();
  };

  const onUpdateValues = (id: string, value: Partial<Condition>) => {
    setConditionsData((prevData) => {
      const index = prevData.findIndex((c) => c.localId === id || c.id === id);
      if (index === -1) return prevData;

      const updatedCondition = { ...prevData[index], ...value };
      getComparisons(updatedCondition);

      return [...prevData.slice(0, index), updatedCondition, ...prevData.slice(index + 1)];
    });
  };

  const onDeleteCondition = (id: string) => {
    setConditionsData((prevData) => {
      let updatedData = [...prevData];
      const conditionToDelete = updatedData.find((c) => c.localId === id);

      if (!conditionToDelete) return prevData;

      if (conditionToDelete.isGroup) {
        updatedData = updatedData.filter(
          (condition) => condition.groupId !== conditionToDelete.groupId && condition.localId !== conditionToDelete.localId
        );
      } else {
        updatedData = updatedData.filter((condition) => condition.localId !== id);
      }

      if (updatedData.length > 0) {
        updatedData[0] = { ...updatedData[0], logicalOperator: 'None' };
      }

      if (conditionToDelete.groupId) {
        recalculateparenthesis();
      }

      return updatedData;
    });
  };

  const onUpdateOperator = (id: string, operator: 'And' | 'Or') => {
    setConditionsData((prevData) => {
      const index = prevData.findIndex((c) => c.localId === id);
      if (index === -1) return prevData;

      const updatedCondition = { ...prevData[index], logicalOperator: operator };
      return [...prevData.slice(0, index), updatedCondition, ...prevData.slice(index + 1)];
    });
  };

  const transformedConditionsForBE = useCallback((): Condition[] => {
    return conditionsData;
  }, [conditionsData]);

  const isConditionEmpty = (condition: Condition) => {
    if (!condition.comparisonType || condition.comparisonType === 'None') {
      return true;
    }

    if (comparisonsTypeWhichDoesNotNeedValue.includes(condition.comparisonType)) return false;

    const shouldBeAbleToSelectBoth = condition.comparisonType === 'IsBiggerBy' || condition.comparisonType === 'IsSmallerBy';

    if (shouldBeAbleToSelectBoth && (!condition.valueDirect || !condition.valueSourceType)) {
      return true;
    }

    if (!condition.valueDirect && !condition.valueFormItemId && !condition.valueSourceType) {
      return true;
    }

    return false;
  };

  const checkIfConditionIsComplete = useCallback((condition: Condition): boolean => {
    if (Array.isArray(condition)) return condition.every(checkIfConditionIsComplete);
    if (isConditionEmpty(condition)) return false;

    return true;
  }, []);

  return {
    conditionsData,
    onAddCondition,
    onDeleteCondition,
    onUpdateValues,
    onUpdateOperator,
    onCreateGroup,
    comparisons,
    transformedConditionsForBE,
    checkIfConditionIsComplete,
    isLoading: isComparisonsLoading,
  };
};
